import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IResponseSrikaNode, ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika'
import { Gathering } from '../../../../../../modules/API/Gathering/gathering'
import { Warehouse } from '../../../../../../modules/API/Warehouse/warehouse'
import { Knisot } from '../../../../../../modules/API/Knisot/knisot'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import { LOAD, LOAD_END } from '../../../../../../../store/actions/actionsTypes'
import { useBarcodeScanner } from '../../../../../../generic/hooks/useBarcodeScanner'
import { useWarehouseLocations, findLocationByCode } from '../../../../../../generic/hooks/useWarehouseLocations'
import { parseEntryBarcode } from '../helpers/parseEntryBarcode'
import { TScanStage, UseProductLocationReturn } from '../types/productLocation'

/** Скан строки входа → скан ячейки → привязка; правка остатка отсканированной строки. */
const useProductLocation = (): UseProductLocationReturn => {
  const [t] = useTranslation('knisa')
  const dispatch = useDispatch()
  const { locations } = useWarehouseLocations()
  const [scannedPallet, setScannedPallet] = useState<ISrikaItem | null>(null)
  const [scannedLocation, setScannedLocation] = useState<IResponseSrikaNode | null>(null)
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [barecode, setBarecode] = useState<string>("")
  const [locationCode, setLocationCode] = useState<string>("")

  const scanStage: TScanStage = scannedPallet ? 'location' : 'entry'
  const scanValue = scanStage === 'entry' ? barecode : locationCode
  const setScanValue = scanStage === 'entry' ? setBarecode : setLocationCode

  const handleScanPallet = async (pallet: string) => {
    try {
      const res = await Gathering.getKnisaByBarcode(pallet)
      setScannedPallet(res.entryLine[0])
    } catch (err) {
      handleNotifications('לא נמצא פריט עם קוד זה', { type: "warning" })
    }
  }

  const handleScanLocation = async (location: string) => {
    if(!scannedPallet) return
    if (!findLocationByCode(locations, location)) {
      handleNotifications('איתור לא נמצא במערכת', { type: "warning" })
      return
    }
    dispatch({type: LOAD})
    try {
      const res = await Warehouse.getEntryDetailsByLocation(location.trim())
      setScannedLocation(res)
    } catch (err) {
      handleNotifications('משהו השתבש', { type: "warning" })
    } finally {
      dispatch({type: LOAD_END})
    }
  }

  const handleScan = async (value: string) => {
    if (scanStage === 'location') {
      setLocationCode(value)
      await handleScanLocation(value)
      return
    }
    // Без EntryNum-DetailNum ни привязать, ни сохранить остаток не получится
    if (!parseEntryBarcode(value)) {
      handleNotifications('מספר משטח לא תקין', { type: 'warning' })
      return
    }
    setBarecode(value)
    await handleScanPallet(value)
  }

  const clearScannedDataLocation = () => {
    setScannedLocation(null)
    setLocationCode("")
  }

  // Убрали кнису — сканер возвращается к баркоду книсы, ячейка без неё не нужна
  const clearScannedDataPallet = () => {
    setScannedPallet(null)
    setBarecode("")
    clearScannedDataLocation()
  }

  const handleBoundPalletToLocation = async () => {
    dispatch({type: LOAD})
    try {
      const key = parseEntryBarcode(barecode)
      if(!key) {
        handleNotifications('קוד כניסה לא תקין', { type: "error" })
        return
      }
      await Warehouse.boundPalletToLocation({
        DetailNum: key.DetailNum,
        EntryNum: key.EntryNum,
        Locating: locationCode
      })
      clearScannedDataPallet()
      handleNotifications('איתור נוסף בהצלחה', { type: "success" })
    } catch (err) {
      handleNotifications('משהו השתבש', { type: "warning" })
    } finally {
      dispatch({type: LOAD_END})
    }
  }

  const openModal = () => {
    setIsOpenModal(true)
  }

  const closeModal = () => {
    setIsOpenModal(false)
  }

  const handleSaveBalance = async (balanceAmount: number) => {
    const key = parseEntryBarcode(barecode)
    if (!scannedPallet || !key) return
    dispatch({type: LOAD})
    try {
      const res = await Knisot.putDetailBalance(key.EntryNum, {
        Details: [{ DetailNum: key.DetailNum, BalanceAmount: balanceAmount }],
      })
      const updated = res.Details.find((detail) => detail.DetailNum === key.DetailNum)
      setScannedPallet((prev) => prev && {
        ...prev,
        BalanceAmount: updated?.BalanceAmount ?? balanceAmount,
        BalanceWeight: updated?.BalanceWeight ?? prev.BalanceWeight,
      })
      closeModal()
      handleNotifications(t('knisa_notification_entryUpdated'), { type: 'success' })
    } catch (err) {
      handleNotifications(t('knisa_notification_genericError'), { type: 'error' })
    } finally {
      dispatch({type: LOAD_END})
    }
  }

  // Пока открыта модалка, железный сканер не должен перехватывать ввод
  useBarcodeScanner({
    onScan: handleScan,
    enabled: !isOpenModal,
    duplicateScanWindowMs: 700,
  })

  return {
    scannedPallet,
    scannedLocation,
    locationCode,
    scanStage,
    scanValue,
    setScanValue,
    handleScan,
    clearScannedDataPallet,
    clearScannedDataLocation,
    handleBoundPalletToLocation,
    canBound: !!scannedPallet && !!scannedLocation,
    isOpenModal,
    openModal,
    closeModal,
    handleSaveBalance,
    balanceModalProps: scannedPallet ? { item: scannedPallet, onSave: handleSaveBalance } : null,
  }
}

export default useProductLocation
